import { cn } from "@/lib/utils";
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShieldCheck } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCart } from '@/contexts/CartContext';
import { useMiniCart } from '@/contexts/MiniCartContext';
import { getCatalogItem, getProductPath, type CatalogId } from '@/lib/products';
import { getShopView, formatPriceEur } from '@/lib/shopView';
import { PaymentLogos } from '@/components/shop/PaymentLogos';
import { LineDiscountLabel } from '@/components/cart/LinePrice';
import { FREE_SHIPPING_THRESHOLD_CENTS, FREE_SHIPPING_THRESHOLD_EUR, calcCartShippingCents, fillShipping } from '@/config/shipping';
import { BUNDLE_INDIVIDUAL_TOTAL, PRODUCT_PRICES } from '@/lib/product-catalog-data';
import { BUNDLE } from '@/lib/products';

const SINGLE_IDS = ['body-care', 'hydration', 'balance'] as const;

/** Cross-sell rules: no set slot with a set; swap when Body-Care/Hydration are in; add set for Balance only. */
export function pickSetCrossSell(ids: string[]): 'swap' | 'add' | null {
  if (ids.length === 0 || ids.includes('bundle')) return null;
  if (ids.includes('body-care') || ids.includes('hydration')) return 'swap';
  return ids.includes('balance') ? 'add' : null;
}

/** Cheapest single product not yet in the cart (only shown while shipping is not free). */
export function pickCheapestMissing(ids: string[]): (typeof SINGLE_IDS)[number] | null {
  const missing = SINGLE_IDS.filter((id) => !ids.includes(id));
  if (!missing.length) return null;
  return missing.reduce((a, b) => (PRODUCT_PRICES[b] < PRODUCT_PRICES[a] ? b : a));
}

export const MiniCart: React.FC = () => {
  const { t, language } = useLanguage();
  const m = t.shop.miniCart;
  const navigate = useNavigate();
  const { isOpen, setOpen, closeMiniCart } = useMiniCart();
  const { items, updateQuantity, removeItem, getItemCount, getCartTotals, promoCode, addItem } = useCart();
  const totals = getCartTotals();
  const fmt = (v: number) => formatPriceEur(v, language);
  const hasSet = items.some((i) => i.id === 'bundle');
  const onlySet = items.length > 0 && items.every((i) => i.id === 'bundle');
  const totalCents = Math.round(totals.total * 100);
  // Same function as the server checkout (create-checkout).
  const shippingCents = calcCartShippingCents({ hasBundle: hasSet, promoFreeShipping: promoCode?.freeShipping === true, discountedTotalCents: totalCents });
  const freeShip = shippingCents === 0;
  const grandTotal = (totalCents + shippingCents) / 100;
  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD_EUR - totals.total);
  const progress = freeShip ? 100 : Math.min(100, (totals.total / FREE_SHIPPING_THRESHOLD_EUR) * 100);
  const ids = items.map((i) => i.id);
  const setMode = pickSetCrossSell(ids);
  const setView = setMode ? getShopView('bundle', language, t.products.bundle.shortDescription) : null;
  const singleId = freeShip ? null : pickCheapestMissing(ids);
  const singleView = singleId ? getShopView(singleId, language, t.products.bundle.shortDescription) : null;
  const singleReachesFree = singleId ? totalCents + Math.round(PRODUCT_PRICES[singleId] * 100) >= FREE_SHIPPING_THRESHOLD_CENTS : false;
  const setSavings = Math.round((BUNDLE_INDIVIDUAL_TOTAL - BUNDLE.price) * 100) / 100;

  const go = (path: string) => {
    closeMiniCart();
    navigate(path);
  };

  return (
    <Sheet open={isOpen} onOpenChange={setOpen}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
        <div className="border-b border-border px-5 py-4 pr-12">
          <SheetTitle className="font-dm-serif text-2xl font-normal">
            {m.title} <span className="font-stolzl text-sm text-muted-foreground">· {m.itemCount.replace('{count}', String(getItemCount()))}</span>
          </SheetTitle>
          <SheetDescription className="sr-only">{m.title}</SheetDescription>
        </div>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-5 px-5 text-center">
            <p className="font-dm-serif text-xl">{m.empty}</p>
            <button
              type="button"
              onClick={() => go('/shop')}
              className="inline-flex h-12 min-w-[200px] items-center justify-center rounded-full bg-h2h-charcoal px-6 text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {m.toShop}
            </button>
          </div>
        ) : (
          <>
            <div className="border-b border-border px-5 py-3">
              <p className="mb-2 text-sm" aria-live="polite">
                {freeShip ? m.shippingFree : (hasSet ? m.shippingRemaining : m.shippingRemainingOrSet).replace('{amount}', fmt(remaining))}
              </p>
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress)}>
                <div className={`h-full rounded-full transition-[width] duration-500 ${freeShip ? "bg-h2h-green" : "bg-ci-orange"}`} style={{ width: `${progress}%` }} />
              </div>
            </div>

            <ul className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
              {items.map((item) => {
                const v = getShopView(item.id as CatalogId, language, t.products.bundle.shortDescription);
                return (
                  <li key={item.id} className="flex gap-3">
                    {v && <img src={v.image} loading="lazy" alt="" aria-hidden="true" className={cn("h-20 w-20 shrink-0 rounded-xl", "object-cover")} />}
                    <div className="min-w-0 flex-1">
                      <Link to={getProductPath(item.id as CatalogId)} onClick={closeMiniCart} className="font-dm-serif text-lg leading-tight hover:underline">
                        {item.name[language]}
                      </Link>
                      {totals.lines[item.id]?.source ? (
                        <>
                          <p className="text-sm" data-discounted-price><span className="font-semibold">{fmt(totals.lines[item.id].unit)}</span> <s className="text-muted-foreground">{fmt(item.price)}</s></p>
                          <LineDiscountLabel item={item} totals={totals} />
                        </>
                      ) : <p className="text-sm text-muted-foreground">{fmt(item.price)}</p>}
                      <div className="mt-2 flex items-center gap-2">
                        <div className="inline-flex items-center rounded-full border border-border" role="group" aria-label={m.quantity}>
                          <button type="button" onClick={() => updateQuantity(item.id, item.quantity - 1)} aria-label={m.decrease} className="inline-flex h-11 w-11 items-center justify-center rounded-full hover:bg-muted">
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="w-6 text-center text-sm font-medium">{item.quantity}</span>
                          <button type="button" onClick={() => updateQuantity(item.id, item.quantity + 1)} aria-label={m.increase} className="inline-flex h-11 w-11 items-center justify-center rounded-full hover:bg-muted">
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                        <button type="button" onClick={() => removeItem(item.id)} aria-label={`${m.remove}: ${item.name[language]}`} className="inline-flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}

              {(setView || singleView) && (
                <li className="space-y-3 rounded-2xl bg-muted/60 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{m.crossSellTitle}</p>
                  {setView && setMode && (
                    <div data-cross="set">
                      <div className="flex items-center gap-3">
                        <img src={setView.image} loading="lazy" alt="" aria-hidden="true" className="h-14 w-14 shrink-0 rounded-lg object-cover" />
                        <div className="min-w-0 flex-1">
                          <Link to={getProductPath('bundle')} onClick={closeMiniCart} className="font-dm-serif leading-tight hover:underline">{setView.name}</Link>
                          <p className="text-sm text-muted-foreground">{fmt(setView.price)}</p>
                        </div>
                        <button type="button" onClick={() => { const it = getCatalogItem('bundle'); if (it) addItem(it); }}
                          className="inline-flex h-11 shrink-0 items-center rounded-full border border-h2h-charcoal px-4 text-sm font-semibold hover:bg-background">
                          {setMode === 'swap' ? m.swapSet : m.add}
                        </button>
                      </div>
                      {setMode === 'swap' && <p className="mt-2 text-xs text-muted-foreground">{m.swapSetText.replace('{amount}', fmt(setSavings))}</p>}
                    </div>
                  )}
                  {singleView && singleId && (
                    <div data-cross="single">
                      <div className="flex items-center gap-3">
                        <img src={singleView.image} loading="lazy" alt="" aria-hidden="true" className="h-14 w-14 shrink-0 rounded-lg object-cover" />
                        <div className="min-w-0 flex-1">
                          <Link to={getProductPath(singleId)} onClick={closeMiniCart} className="font-dm-serif leading-tight hover:underline">{singleView.name}</Link>
                          <p className="text-sm text-muted-foreground">{fmt(singleView.price)}</p>
                        </div>
                        <button type="button" onClick={() => { const it = getCatalogItem(singleId); if (it) addItem(it); }}
                          className="inline-flex h-11 shrink-0 items-center rounded-full border border-h2h-charcoal px-4 text-sm font-semibold hover:bg-background">
                          {m.add}
                        </button>
                      </div>
                      {singleReachesFree && <p className="mt-2 text-xs font-semibold text-foreground">{m.reachesFree}</p>}
                    </div>
                  )}
                </li>
              )}
            </ul>

            <div className="space-y-3 border-t border-border px-5 py-4">
              {promoCode && promoCode.discountPercent > 0 ? (
                <p className="flex items-center justify-between text-sm font-semibold">
                  <span data-code-active>{m.codeActive.replace('{code}', promoCode.code)} · −{promoCode.discountPercent} %</span>
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">{onlySet ? t.shop.pdp.promoExcluded : m.promoHint}</p>
              )}
              <dl className="space-y-1 text-sm">
                <div className="flex items-center justify-between"><dt>{m.subtotal}</dt><dd>{fmt(totals.total)}</dd></div>
                <div className="flex items-center justify-between" data-mini-shipping><dt>{m.shipping}</dt><dd>{freeShip ? m.shippingFreeValue : fmt(shippingCents / 100)}</dd></div>
                <div className="flex items-center justify-between text-base font-bold" data-mini-total><dt>{m.total}</dt><dd className="text-xl">{fmt(grandTotal)}</dd></div>
              </dl>
              <button
                type="button"
                onClick={() => go('/bestellung')}
                className="inline-flex h-14 w-full items-center justify-center rounded-full bg-h2h-charcoal text-base font-semibold text-white transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {m.checkout}
              </button>
              <p className="text-center text-xs text-muted-foreground">{m.vatNote}</p>
              <p className="text-center text-xs text-muted-foreground" data-mini-delivery>{fillShipping(m.deliveryLine, language)}</p>
              <p className="text-center text-xs text-muted-foreground" data-shipping-country-notice>{t.shippingUnits.countryNotice}</p>
              <PaymentLogos className="justify-center" />
              <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                <ShieldCheck className="h-4 w-4" aria-hidden="true" /> {m.trust}
              </p>
              <p className="text-center text-xs font-semibold" data-mini-guarantee>{t.shop.pdp.guaranteeBadge}</p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
